<template>
    <div class="bits-searchbar">
        <input
            ref="input"
            type="search"
            :value="text"
            @input="onInput(($event.target as HTMLInputElement).value)"
            @keydown.enter="submit"
            @keydown.down.prevent="cycle(1)"
            @keydown.up.prevent="cycle(-1)"
            @focus="onFocus"
            @blur="onBlur"
            placeholder="Hva leter du etter?"
            autocomplete="off"
        />
        <button type="button" @click="submit">Søk</button>
        <!-- Seksjonene ligger i hver sin wrapper slik at fullbredde-varianten kan stokke om
             på dem med CSS `order` (innhold før produkter, som i den gamle Clerk-visningen).
             I kompakt visning er wrapperen `display: contents`, så DOM-en oppfører seg som
             før for temaene som skinner søket selv. -->
        <div
            v-if="visible && hasContent"
            class="bits-searchbar__suggestions"
            :class="{ 'bits-searchbar__suggestions--fullwidth': isFullWidth }"
        >
            <div v-if="categories.length > 0" class="bits-searchbar__section bits-searchbar__section--categories">
                <div class="bits-searchbar__section-label">Kategorier</div>
                <div class="bits-searchbar__links">
                    <a
                        v-for="c in categories"
                        :key="'c' + c.id"
                        :href="c.url"
                        :title="categoryLabel(c)"
                        @mousedown.prevent="goTo(c.url)"
                    ><span
                        v-if="c.path && c.path.length > 0"
                        class="bits-searchbar__category-path"
                    >{{ c.path.join(' › ') }} › </span>{{ c.name }}</a>
                </div>
            </div>
            <div v-if="suggestionRow.length > 0" class="bits-searchbar__section bits-searchbar__section--suggestions">
                <div class="bits-searchbar__section-label">Søkeforslag</div>
                <div class="bits-searchbar__links">
                    <a
                        v-for="(t, i) in suggestionRow"
                        :key="'s' + t"
                        href="#"
                        :class="{ 'is-active': i === active }"
                        @mousedown.prevent="pick(t)"
                    ><strong v-if="matchedPart(t)">{{ matchedPart(t) }}</strong>{{ completionPart(t) }}</a>
                </div>
            </div>
            <div v-if="products.length > 0" class="bits-searchbar__section bits-searchbar__section--products">
                <div class="bits-searchbar__section-label">Produkter</div>
                <a
                    v-for="p in products"
                    :key="'p' + p.id"
                    class="bits-searchbar__product"
                    :href="p.url"
                    @mousedown.prevent="goTo(p.url)"
                >
                    <img v-if="p.imageUrl" :src="p.imageUrl" alt="" loading="lazy" />
                    <span class="bits-searchbar__product-name">{{ p.name }}</span>
                    <!-- Tilbud: førprisen gjennomstreket foran tilbudsprisen, samme
                         rekkefølge som temaenes produktkort. Uten den leses tilbuds-
                         prisen som ordinærpris. -->
                    <span v-if="p.price || p.oldPrice" class="bits-searchbar__product-prices">
                        <span v-if="p.oldPrice" class="bits-searchbar__product-oldprice">{{ p.oldPrice }}</span>
                        <span v-if="p.price" class="bits-searchbar__product-price">{{ p.price }}</span>
                    </span>
                </a>
            </div>
            <div v-if="pages.length > 0" class="bits-searchbar__section bits-searchbar__section--pages">
                <div class="bits-searchbar__section-label">{{ contentHeading }}</div>
                <a
                    v-for="(pg, i) in pages"
                    :key="'pg' + pg.url + i"
                    class="bits-searchbar__page"
                    :href="pg.url"
                    @mousedown.prevent="goTo(pg.url)"
                >
                    <!-- Samme radoppsett som produktene over: bilderamma tegnes også
                         uten bilde, ellers står titlene i to ulike venstremarger. -->
                    <span class="bits-searchbar__page-thumb">
                        <img v-if="pg.imageUrl" :src="pg.imageUrl" alt="" loading="lazy" />
                    </span>
                    <span class="bits-searchbar__page-title">{{ pg.title }}</span>
                    <small v-if="pg.source" class="bits-searchbar__page-source">{{ pg.source }}</small>
                </a>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { autoComplete, type AutoCompleteProductDto, type CategorySuggestionDto, type ExternalResultDto, type PopularSearchDto } from './api';

const props = defineProps<{
    initialQuery?: string;
    layout?: string;
    contentHeading?: string;
}>();

// Fullbredde speiler oppsettet butikkene hadde med Clerk: panelet dekker sidebredden,
// innholdstreffene ligger over produktene, og begge vises som kort i rutenett.
const isFullWidth = computed(() => props.layout === 'fullwidth');
const contentHeading = computed(() => props.contentHeading || 'Innholdssider');

const text = ref<string>(props.initialQuery ?? '');
const tokens = ref<Array<{ token: string; count: number }>>([]);
const categories = ref<CategorySuggestionDto[]>([]);
const products = ref<AutoCompleteProductDto[]>([]);
const popular = ref<PopularSearchDto[]>([]);
const pages = ref<ExternalResultDto[]>([]);
const visible = ref<boolean>(false);
const active = ref<number>(-1);
const hasContent = computed(() =>
    tokens.value.length > 0 || products.value.length > 0 ||
    popular.value.length > 0 || categories.value.length > 0 ||
    pages.value.length > 0);

// Full sti som tooltip/skjermleser-tekst: lenketeksten viser den samme stien, men der
// er forfedrene dempet, og en kunde som holder musa over vil ha hele navnet i klartekst.
function categoryLabel(c: CategorySuggestionDto): string {
    return c.path && c.path.length > 0 ? [...c.path, c.name].join(' › ') : c.name;
}

// Fet utheving av delen kunden alt har skrevet («**adidas** crw», som Clerk):
// forslaget deles i matchet prefiks og fullføring. Matcher ikke forslaget det
// skrevne (populære søk kan avvike), vises hele uthevet-fritt.
function matchedPart(suggestion: string): string {
    const q = text.value.trim().toLowerCase();
    return q && suggestion.toLowerCase().startsWith(q) ? suggestion.slice(0, q.length) : '';
}

function completionPart(suggestion: string): string {
    return suggestion.slice(matchedPart(suggestion).length);
}

// Én «Søkeforslag»-rad som i gammel design: populære søk først, så ordforslag.
const suggestionRow = computed(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const k of [...popular.value.map(p => p.keyword), ...tokens.value.map(t => t.token)]) {
        const key = k.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(k);
        if (out.length >= 6) break;
    }
    return out;
});
let pending: AbortController | null = null;
let debounceId: number | null = null;

function onInput(value: string): void {
    text.value = value;
    if (debounceId) window.clearTimeout(debounceId);
    debounceId = window.setTimeout(() => fetchSuggestions(value), 120);
}

async function fetchSuggestions(q: string): Promise<void> {
    // Tom/kort q: hent kun populære søk (vises på fokus før man har skrevet noe).
    const effective = q.trim().length < 2 ? '' : q;
    pending?.abort();
    const ctrl = new AbortController();
    pending = ctrl;
    try {
        const r = await autoComplete(effective, ctrl.signal);
        if (pending !== ctrl) return;
        tokens.value = r.tokens ?? [];
        categories.value = r.categories ?? [];
        products.value = r.products ?? [];
        popular.value = r.popularSearches ?? [];
        pages.value = r.pages ?? [];
        visible.value = hasContent.value;
        active.value = -1;
    } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        console.error('autocomplete failed', e);
    }
}

function onFocus(): void {
    if (hasContent.value) {
        visible.value = true;
        return;
    }
    void fetchSuggestions(text.value);
}

function goTo(url: string): void {
    window.location.href = url;
}

function cycle(direction: number): void {
    if (suggestionRow.value.length === 0) return;
    let next = active.value + direction;
    if (next < 0) next = suggestionRow.value.length - 1;
    if (next >= suggestionRow.value.length) next = 0;
    active.value = next;
}

function pick(token: string): void {
    text.value = token;
    visible.value = false;
    submit();
}

function submit(): void {
    const q = (active.value >= 0 ? suggestionRow.value[active.value] : text.value).trim();
    if (!q) return;
    window.location.href = `/bits-search?q=${encodeURIComponent(q)}`;
}

function onBlur(): void {
    window.setTimeout(() => { visible.value = false; }, 150);
}

watch(() => props.initialQuery, (v) => { text.value = v ?? ''; });
</script>
